import { useCallback, useEffect, useState } from 'react';
import { apiService } from '../services/apiService';
import { FulfillmentContextSummary } from '../types/export';

// Per-partner cache for the session; retry() evicts the entry.
const cache = new Map<string, FulfillmentContextSummary[]>();

interface State {
  data: FulfillmentContextSummary[];
  loading: boolean;
  error: string | null;
}

export function useFulfillmentContexts(partnerId: string) {
  const [state, setState] = useState<State>({ data: cache.get(partnerId) ?? [], loading: false, error: null });
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!partnerId) {
      setState({ data: [], loading: false, error: null });
      return;
    }
    const cached = cache.get(partnerId);
    if (cached) {
      setState({ data: cached, loading: false, error: null });
      return;
    }

    let cancelled = false;
    setState({ data: [], loading: true, error: null });
    apiService
      .listFulfillmentContexts(partnerId)
      .then((data) => {
        cache.set(partnerId, data);
        if (!cancelled) setState({ data, loading: false, error: null });
      })
      .catch((error) => {
        if (!cancelled) setState({ data: [], loading: false, error: error?.response?.data?.message ?? error?.message ?? 'Failed to load fulfillment contexts' });
      });
    return () => {
      cancelled = true;
    };
  }, [partnerId, attempt]);

  const retry = useCallback(() => {
    cache.delete(partnerId);
    setAttempt((n) => n + 1);
  }, [partnerId]);

  return { ...state, retry };
}
